// ---------------------------------------------------------------------------
// Runtime configuration — validated at module load.
//
// Why fail-fast: a missing env var in a production build silently falling
// back to '' makes Amplify throw deep inside its own code with an opaque
// error a developer cannot pattern-match. Throwing here, at module load,
// means the page surfaces the React ErrorBoundary's "Something went wrong"
// shell with the underlying cause in the browser console instead of a
// half-rendered tree with confusing downstream failures.
//
// Cognito User Pool IDs + Client IDs + API URLs are NOT secrets. They are
// public identifiers — clients hold them, they are reachable by anyone who
// loads the page. Treating them as secrets would not improve security; the
// only secret in the Cognito-SPA model is the user's password (held by
// Cognito itself, never by us).
//
// REACT_APP_REGION keeps its 'us-east-1' fallback because a missing region
// is a sensible-default situation, not a misconfiguration. The four other
// values have NO production-safe default and must be set explicitly by the
// deployment pipeline.
// ---------------------------------------------------------------------------

// IMPORTANT: read each var via DOTTED access (`process.env.REACT_APP_X`), NOT
// computed (`process.env[name]`). Only dotted paths are substituted by Vite's
// build-time `define` (vite.config.mjs), which inlines the value into the
// bundle. Computed access can't be statically substituted, so it falls back to
// the runtime `window.process.env` shim — which is an INLINE <script> that the
// deployed CSP (`script-src 'self'`, no unsafe-inline/nonce/hash — see
// template.yml) BLOCKS, leaving the values unreadable and blank-screening the
// app. Passing the dotted value in keeps `define` able to inline it; `name` is
// only for the error message.
function requireEnv(name, value) {
  if (!value || value.trim() === '') {
    throw new Error(
      `Configuration error: ${name} is not set. ` +
        `In production this is set at build time by ui/developer-portal/pipelines/deploy-pipeline.yml. ` +
        `For local dev, set it in .env.local or pass it on the command line to npm start.`,
    );
  }
  return value;
}

export const COGNITO_CONFIG = {
  region: process.env.REACT_APP_REGION || 'us-east-1',
  userPoolId: requireEnv('REACT_APP_DEVELOPER_USER_POOL_ID', process.env.REACT_APP_DEVELOPER_USER_POOL_ID),
  userPoolClientId: requireEnv('REACT_APP_DEVELOPER_APP_CLIENT_ID', process.env.REACT_APP_DEVELOPER_APP_CLIENT_ID),
};

// The deploy pipeline reads these two URLs from the edge stack's CloudFormation
// outputs, which end in a slash (`https://api.example.com/`). The call sites that
// build a URL by hand (`${developerApiBase}/developer/scoped-token` in
// auth/AuthContext.js, `/developer/request-access`, `/developer/redeem-invite`)
// then send `//developer/...`, which the API's root-mapped origin does not match
// (404, so the browser's CORS preflight fails). Trim the trailing slashes once,
// here, so every caller joins a base without one.
function stripTrailingSlashes(url) {
  return url.replace(/\/+$/, '');
}

export const API_CONFIG = {
  developerApiBase: stripTrailingSlashes(
    requireEnv('REACT_APP_DEVELOPER_API_URL', process.env.REACT_APP_DEVELOPER_API_URL),
  ),
  partnerApiBase: stripTrailingSlashes(
    requireEnv('REACT_APP_PARTNER_API_URL', process.env.REACT_APP_PARTNER_API_URL),
  ),
};

// Optional anti-bot signup challenge (Cloudflare Turnstile — ADR-0021). This is
// the PUBLIC site key, safe to embed in the bundle. Read via DOTTED access so
// Vite's build-time `define` inlines it (see the requireEnv note above re: the
// CSP-blocked runtime shim). NOT required: when unset (local dev, or before
// CAPTCHA is provisioned) the signup form renders no widget and sends no token,
// and the backend CAPTCHA_MODE gate (OFF by default) decides whether that is OK.
export const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
